import { Component, Element, Event, EventEmitter, h, Host, Prop } from '@stencil/core';

import { StzhCardNavigationClickEvent } from '../../../index';
import { hasSlot } from '../../../utils/utils';

/**
 * @slot heading - Heading text (alternative for heading property)
 * @slot lead - Lead text (alternative for lead property)
 */
@Component({
  tag: 'stzh-card-navigation',
  styleUrl: 'stzh-card-navigation.scss',
  scoped: true,
})
export class StzhCardNavigation {
  /** Href of card */
  @Prop({ reflect: true }) href: string;

  /** target if the button is used as link (if href used)  */
  @Prop() target: string;

  /**
   * For Adobe Analytics.
   * Will be rendered as value of `s-object-id` attribute to the link elements.
   * Default value will be taken from `heading` prop/slot.
   */
  @Prop() analyticsId: string;

  /** Heading (alternative for heading slot) */
  @Prop() heading: string = '';

  /** Heading level */
  @Prop() headingLevel: '1' | '2' | '3' | '4' = '3';

  /** Lead text (alternative for lead slot) */
  @Prop() lead: string = '';

  /** Automatic Hyphens */
  @Prop({ reflect: true }) hyphensEnabled: boolean = false;

  @Element() element: HTMLStzhListElement;

  /** Click event (if href is used) */
  @Event() stzhClick: EventEmitter<StzhCardNavigationClickEvent>;

  private titleLinkElement: HTMLAnchorElement | HTMLButtonElement;
  private headingTextElement: HTMLDivElement;

  private handleCardClick = (originalEvent: MouseEvent) => {
    this.stzhClick.emit({
      component: 'stzh-card-navigation',
      originalEvent,
      href: this.href,
    });
  };

  componentDidRender() {
    requestAnimationFrame(() => {
      const objectId = this.analyticsId || this.headingTextElement?.innerText;

      if (objectId) {
        this.titleLinkElement?.setAttribute('s-object-id', objectId);
      }
    });
  }

  render() {
    const hasLead = !!this.lead || hasSlot(this.element, 'lead');

    const classes = {
      'stzh-card-navigation': true,
      'stzh-card-navigation--has-lead': hasLead,
      'stzh-card-navigation--hyphens': this.hyphensEnabled,
    };

    const Heading = `h${this.headingLevel}`;
    const TitleLink = this.href ? 'a' : 'button';

    return (
      <Host has-lead={hasLead}>
        <article class={classes} aria-label={this.heading} role="listitem">
          <div class="stzh-card-navigation__wrapper">
            <Heading class="stzh-card-navigation__title">
              <TitleLink
                ref={el => (this.titleLinkElement = el as HTMLAnchorElement | HTMLButtonElement)}
                class="stzh-card-navigation__title-link"
                href={this.href}
                target={this.target}
                onClick={this.handleCardClick}
              >
                {this.heading ? (
                  <div ref={el => (this.headingTextElement = el as HTMLDivElement)} innerHTML={this.heading}></div>
                ) : (
                  <div ref={el => (this.headingTextElement = el as HTMLDivElement)}>
                    <slot name="heading"></slot>
                  </div>
                )}
              </TitleLink>
            </Heading>

            <div class="stzh-card-navigation__lead">{this.lead ? this.lead : <slot name="lead"></slot>}</div>
          </div>

          <div class="stzh-card-navigation__arrow">
            <stzh-icon class="stzh-card-navigation__arrow-icon" name="arrow-right"></stzh-icon>
          </div>
        </article>
      </Host>
    );
  }
}
